import { ArrowLeft } from 'lucide-react';

interface TermsOfServiceProps {
  onBack?: () => void;
}

export default function TermsOfService({ onBack }: TermsOfServiceProps) {
  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      window.history.back();
    }
  };

  return (
    <div className="flex min-h-screen w-full flex-col bg-[#f8f5f5] overflow-x-hidden">
      {/* Header */}
      <div className="flex items-center p-4 pb-2 justify-between sticky top-0 bg-[#f8f5f5]/80 backdrop-blur-md z-10">
        <button
          onClick={handleBack}
          className="size-10 rounded-full hover:bg-slate-200/50 flex items-center justify-center transition-colors"
        >
          <ArrowLeft size={24} className="text-slate-600" />
        </button>
        <h1 className="text-lg font-bold text-slate-900">Terms of Service</h1>
        <div className="size-10" />
      </div>

      <div className="px-5 pb-8 mt-2">
        <div className="bg-white rounded-2xl shadow-sm p-5 space-y-5">
          <p className="text-xs text-slate-400">Last updated: March 25, 2026</p>

          <Section title="The Short Version">
            <p>
              Nudge is a free app that helps you maintain your personal relationships. Use it
              respectfully, don&apos;t abuse the service, and we&apos;ll do our best to keep it
              running well for you. You own your data, and you can leave anytime.
            </p>
          </Section>

          <Section title="1. What Nudge Is">
            <p>
              Nudge (&quot;the Service&quot;) is a personal relationship management app that lets
              you track contacts, log interactions, and receive reminders to stay in touch with the
              people in your life. It is provided by Nudge (&quot;we&quot;, &quot;us&quot;).
            </p>
          </Section>

          <Section title="2. Your Account">
            <ul className="list-disc pl-5 space-y-1">
              <li>You must be at least 13 years old to use Nudge.</li>
              <li>
                You&apos;re responsible for keeping your account credentials secure. If you suspect
                unauthorized access, reset your password immediately.
              </li>
              <li>
                You can use Nudge in guest mode without an account, but your data will only be
                stored locally on your device.
              </li>
              <li>
                One account per person. Don&apos;t create accounts on behalf of others without their
                knowledge.
              </li>
            </ul>
          </Section>

          <Section title="3. What You Can Do">
            <ul className="list-disc pl-5 space-y-1">
              <li>Add contacts and log interactions to track your relationships</li>
              <li>Receive push notifications and reminders</li>
              <li>Export all your data at any time</li>
              <li>Delete your account and all associated data</li>
              <li>Use the app for free — there are no hidden charges</li>
            </ul>
          </Section>

          <Section title="4. What You Shouldn't Do">
            <p>Please don&apos;t:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>
                Use Nudge to store data about people for purposes of harassment, stalking, or any
                illegal activity
              </li>
              <li>Attempt to access other users&apos; accounts or data</li>
              <li>Reverse engineer, scrape, or overload our servers</li>
              <li>Use automated tools to create accounts or submit data</li>
              <li>Upload content that is illegal, harmful, or violates others&apos; rights</li>
            </ul>
            <p className="mt-2">
              We reserve the right to suspend or terminate accounts that violate these terms.
            </p>
          </Section>

          <Section title="5. Your Data">
            <ul className="list-disc pl-5 space-y-1">
              <li>
                <strong>You own your data.</strong> The contacts, notes, and interactions you add
                belong to you.
              </li>
              <li>
                We only use your data to provide the Service (see our Privacy Policy for details).
              </li>
              <li>You can export or delete your data at any time through the app settings.</li>
              <li>When you delete your account, all data is permanently removed within 30 days.</li>
            </ul>
          </Section>

          <Section title="6. The Service">
            <ul className="list-disc pl-5 space-y-1">
              <li>
                Nudge is provided <strong>&quot;as is&quot;</strong>. We do our best to keep it
                available and bug-free, but we can&apos;t guarantee 100% uptime or that everything
                will always work perfectly.
              </li>
              <li>
                We may update, modify, or discontinue features at any time. We&apos;ll try to give
                you notice for significant changes.
              </li>
              <li>
                Nudge is currently free. If we ever introduce paid features, existing free
                functionality will remain free.
              </li>
            </ul>
          </Section>

          <Section title="7. Limitation of Liability">
            <p>
              To the maximum extent permitted by law, Nudge and its team are not liable for any
              indirect, incidental, or consequential damages arising from your use of the Service.
              Our total liability is limited to the amount you&apos;ve paid us (which is $0 if
              you&apos;re using the free service).
            </p>
          </Section>

          <Section title="8. Changes to These Terms">
            <p>
              We may update these terms from time to time. If we make significant changes,
              we&apos;ll notify you through the app. Continued use after notification means you
              accept the updated terms.
            </p>
          </Section>

          <Section title="9. Contact Us">
            <p>
              Questions about these terms? Reach out through the Support & Feedback section in the
              app, or email us at <strong>legal@trynudge.xyz</strong>.
            </p>
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="text-sm font-bold text-slate-800 mb-2">{title}</h2>
      <div className="text-[13px] text-slate-600 leading-relaxed">{children}</div>
    </div>
  );
}
